import { useEffect, useState } from "react";
import { LayerTypeRightClickMenuProps } from "../../types";
import { getDeckInstance } from "src/js/plugins/deck.gl/deckInstance";
import { getDeckLayerInstanceIds } from "react-migration/lib/map/getLayerInstances";
import { sitePickResolver } from "../AlwaysOnSitesLayerType/sitesClickResolver";
import { PickingInfo } from "deck.gl";
import { getSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { isSiteBoundary } from "./utils";
import { isDefined } from "react-migration/lib/util/isDefined";
import { SiteOverview } from "react-migration/domains/sites/typings/Site";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { RightClick } from "react-migration/components/RightClick";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { userStore } from "src/js/stores/user/store";
import { dateString, SupportedLocale } from "src/js/util/dates";
import { Avatar } from "react-migration/components/Avatar";
import { SitesClusteredViewportSearchMapLayer } from "react-migration/domains/sites/map/SitesClusteredViewportSearchMapLayer";
import { SitesMVTLayer } from "react-migration/domains/sites/map/SitesMVTLayer";

const SHARED_COLOR_HEX = "#01EBC7";

interface SiteMenuItem {
  id: string;
  stage: SiteOverview["_stage"];
  title: SiteOverview["title"];
  sharedByUser?: SiteOverview["sharedByUser"];
  selectionFeature: SelectionFeature;
}

export function SiteRightClickMenu({
  rightClickLocation,
  setSelection,
  setHoveredFeature,
}: LayerTypeRightClickMenuProps) {
  const [menuItems, setMenuItems] = useState<SiteMenuItem[]>();
  const [savedSitesMenuItems, setSavedSitesMenuItems] = useState<SiteMenuItem[]>([]);
  const [sharedSitesMenuItems, setSharedSitesMenuItems] = useState<SiteMenuItem[]>([]);

  useEffect(() => {
    function getSitePicksFromDeck() {
      const [x, y] = rightClickLocation.deckCoordinate;

      const deck = getDeckInstance();
      const layerIds = getDeckLayerInstanceIds([SitesClusteredViewportSearchMapLayer, SitesMVTLayer]);

      return deck?.pickMultipleObjects({ x, y, layerIds });
    }

    async function enrichSitePicks(picks: PickingInfo<unknown>[]) {
      const results = (
        await Promise.all(
          picks.map(async (pick) => {
            if (!isSiteBoundary(pick.object) || !pick.object.properties.id) return;

            return Promise.all([getSiteById(pick.object.properties.id), sitePickResolver(pick)]);
          })
        )
      )
        .filter(isDefined)
        .map(([site, selectionFeature]) => {
          if (!selectionFeature || !site) return;

          return {
            id: site._id,
            stage: site._stage,
            title: site.title,
            sharedByUser: site.sharedByUser,
            selectionFeature,
          };
        })
        .filter(isDefined);

      setMenuItems(results);
    }

    const sitePicks = getSitePicksFromDeck();
    sitePicks && enrichSitePicks(sitePicks);
  }, [rightClickLocation.deckCoordinate]);

  useEffect(() => {
    if (!menuItems) return;

    const savedSites = menuItems.filter((item) => item.selectionFeature.type === SelectionType.SITE);
    const sharedSites = menuItems.filter((item) => item.selectionFeature.type === SelectionType.SHARED_SITE);

    setSavedSitesMenuItems(savedSites);
    setSharedSitesMenuItems(sharedSites);
  }, [menuItems]);

  if (!savedSitesMenuItems?.length && !sharedSitesMenuItems?.length) return null;

  return (
    <>
      <RightClickGroup
        menuItems={savedSitesMenuItems}
        setHoveredFeature={setHoveredFeature}
        setSelection={setSelection}
        title="Saved Sites"
      />
      <RightClickGroup
        menuItems={sharedSitesMenuItems}
        setHoveredFeature={setHoveredFeature}
        setSelection={setSelection}
        title="Shared with me"
        isShared={true}
      />
    </>
  );
}

const RightClickGroup = ({
  menuItems,
  setHoveredFeature,
  setSelection,
  title,
  isShared = false,
}: {
  menuItems: SiteMenuItem[];
  setHoveredFeature(selection?: SelectionFeature): void;
  setSelection: (feature: SelectionFeature) => void;
  title: string;
  isShared?: boolean;
}) => {
  const { t } = useTranslation();
  const locale = userStore.locale as SupportedLocale;
  if (menuItems.length === 0) return null;

  const tooltipText = (menuItem: SiteMenuItem) =>
    isShared
      ? t("sites.right_click.shared_by", {
          firstname: menuItem.sharedByUser?.firstname,
          surname: menuItem.sharedByUser?.surname,
          date: menuItem.sharedByUser?.date ? dateString(menuItem.sharedByUser.date, locale) : "",
        })
      : menuItem.title;

  const leadComponent = (menuItem: SiteMenuItem) =>
    isShared ? (
      <Avatar
        firstName={menuItem.sharedByUser?.firstname}
        lastName={menuItem.sharedByUser?.surname}
        className="border-none"
      />
    ) : (
      <div
        className="w-3 h-3 rounded-sm"
        style={{ background: !!menuItem.sharedByUser ? SHARED_COLOR_HEX : menuItem.stage?.color }}
      />
    );

  return (
    <RightClick.Group title={title}>
      {menuItems.map((menuItem) => (
        <RightClick.Item
          key={menuItem.id}
          onMouseEnter={() => setHoveredFeature(menuItem.selectionFeature)}
          leadComponent={leadComponent(menuItem)}
          onMouseLeave={() => setHoveredFeature()}
          onClick={() => setSelection(menuItem.selectionFeature)}
        >
          <div className="overflow-hidden truncate" title={tooltipText(menuItem)}>
            {menuItem.title}
          </div>
        </RightClick.Item>
      ))}
    </RightClick.Group>
  );
};
